import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { AlertCircle, ArrowLeft, Loader2 } from "lucide-react";
import { getSignalsIntradayAlgoSignalsGet } from "@/client-admin";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { buttonVariants } from "@/components/ui/button";
import { SignalsTable } from "@/components/admin/SignalsTable/SignalsTable";
import { checkSuperUserPermission, getErrorDetails } from "@/lib/utils";

type SignalsSearch = {
  last_update: string;
  intraday_code: number;
};

export const Route = createFileRoute("/_superuser/admin/algo-trading/signals")({
  component: SignalsPage,
  validateSearch: (search: Record<string, unknown>): SignalsSearch => ({
    last_update: search.last_update as string,
    intraday_code: Number(search.intraday_code),
  }),
  beforeLoad: () => {
    checkSuperUserPermission("algo-trading");
  },
});

function SignalsPage() {
  const { last_update, intraday_code } = Route.useSearch();
  const queryKey = ["intraday-algo-signals", last_update, intraday_code];

  const {
    data: signalsData,
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: () =>
      getSignalsIntradayAlgoSignalsGet({
        lastUpdate: last_update,
        intradayCode: intraday_code,
      }),
    refetchInterval: 60_000,
  });

  const formattedDate = format(new Date(last_update), "yyyy-MM-dd HH:mm");

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-4">
        <Link className={buttonVariants({ variant: "outline", size: "sm" })} to="/admin/algo-trading">
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back
        </Link>
        <h2 className="text-lg font-semibold">Run: {formattedDate}</h2>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{getErrorDetails(error)}</AlertDescription>
        </Alert>
      )}

      {signalsData && <SignalsTable rows={signalsData.data} />}
    </div>
  );
}
